import React, { useMemo, useState } from 'react';
import theme from './OptionGroupProductsEdit.module.scss';
import { useAsyncStateWithIsLoading, useMergeState } from '@/utils/hooks';
import { ProjectCatalogProductsSearchOptions, searchProjectCatalogProducts } from '@/services/project-catalog';
import { arrayReplace } from '@ygtechnologies/rubus-frontend-utilities';
import { isSelectMultiValue, FilterButtons, FilterPopOut, LoadingIndicator, Select, Sortable, SortEvent, useIsFilterModified } from '@ygtechnologies/bygr-frontend-component-library';
import { Button, NoItemsCard } from '@ygtechnologies/rubus-ui';
import { Trans } from '@lingui/react/macro';
import { ItemCategory, ProductSourceCatalog, ProjectCatalogProduct, ProjectFeeSet } from '@/types';
import { arrayMove } from '@ygtechnologies/bygr-frontend-component-library';
import { useSelector } from 'react-redux';
import { AvailableProductCard } from './AvailableProductCard';
import { SelectedProductCard } from './SelectedProductCard';
import { faArrowDownWideShort } from '@fortawesome/pro-light-svg-icons';
import { CatalogProductThumbnails } from '../../products/ProductThumbnails';
import ItemCategoryDisplay from '@/components/item-categories/ItemCategoryDisplay';
import ItemCategoryFilter from '@/components/filters/ItemCategoryFilter';
import { OptionGroupProductsEditItem } from './OptionGroupProductsEdit.types';
import { IState } from '@/state/IState';
import { expandCategoryIdsToIncludeDescendants, findTopLevelCategory } from '@/utils/categories';

const emptyFilter = () => ({ searchText: '', itemCategoryIds: [] as number[] });

function renderDropdownProduct(product: ProjectCatalogProduct) {
    return (
        <div className={theme.selectItem}>
            <div>
                <CatalogProductThumbnails catalog={ProductSourceCatalog.Project} images={product.images} size={50} />
            </div>
            <div className={theme.details}>
                <small>
                    <ItemCategoryDisplay value={product.itemCategoryId} />
                </small>
                {product.name}
            </div>
        </div>
    );
}

export interface OptionGroupProductsEditProps {
    projectId: number;
    value: OptionGroupProductsEditItem[];
    selectedFeeSet: ProjectFeeSet | null;
    onChange(newValue: OptionGroupProductsEditItem[]): void;
    // Effective pricing mode for seeding. Provided only for a brand-new group; left
    // undefined for duplicate/edit so loaded prices are preserved.
    absolutePricing?: boolean;
}

export const OptionGroupProductsEdit: React.FC<OptionGroupProductsEditProps> = ({
    value,
    onChange,
    projectId,
    selectedFeeSet,
    absolutePricing,
}) => {
    const itemCategories = useSelector((state: IState) => state.global.itemCategories);
    const [isFilterOpen, setIsFilterOpen] = useState(false);
    const [filters, mergeFilters] = useMergeState<ProjectCatalogProductsSearchOptions>({
        projectId,
        searchText: '',
        itemCategoryIds: null,
    });
    const filterModified = useIsFilterModified(
        { searchText: filters.searchText ?? '', itemCategoryIds: filters.itemCategoryIds ?? [] },
        emptyFilter
    );
    const apiFilters = useMemo(() => {
        const itemCategoryIds =
            filters.itemCategoryIds && filters.itemCategoryIds.length > 0 && itemCategories
                ? expandCategoryIdsToIncludeDescendants(filters.itemCategoryIds, itemCategories)
                : null;
        return { ...filters, itemCategoryIds };
    }, [filters, itemCategories]);
    const [products, , , isLoading] = useAsyncStateWithIsLoading(
        () => searchProjectCatalogProducts(apiFilters),
        [apiFilters],
        null
    );

    const filteredProducts = useMemo(() => {
        if (!products) {
            return [];
        }

        const selectedIds = value.map((x) => x.product.id);
        let filtered = products.filter(
            (p) => p.permissions && p.permissions.canLink && !selectedIds.includes(p.id)
        );

        const normalizedSearchText = filters.searchText?.trim().toLowerCase();
        if (normalizedSearchText) {
            filtered = filtered.filter((product) => product.name.toLowerCase().includes(normalizedSearchText));
        }

        if (value.length > 0 && itemCategories?.length > 0) {
            const selectedRootIds = new Set(
                value
                    .map((x) => findTopLevelCategory(itemCategories, x.product.itemCategoryId ?? 0))
                    .filter((cat): cat is ItemCategory => cat != null)
                    .map((cat) => cat.id)
            );
            if (selectedRootIds.size > 0) {
                filtered = [...filtered].sort((a, b) => {
                    const aRoot = a.itemCategoryId ? findTopLevelCategory(itemCategories, a.itemCategoryId)?.id : null;
                    const bRoot = b.itemCategoryId ? findTopLevelCategory(itemCategories, b.itemCategoryId)?.id : null;
                    const aMatch = aRoot != null && selectedRootIds.has(aRoot) ? 1 : 0;
                    const bMatch = bRoot != null && selectedRootIds.has(bRoot) ? 1 : 0;
                    return bMatch - aMatch;
                });
            }
        }

        return filtered;
    }, [products, value, itemCategories, filters.searchText]);

    const handleRemove = (item: OptionGroupProductsEditItem) => {
        let newValue = value.filter((x) => x !== item);
        // If the removed product was the default, promote the first remaining row. In
        // absolute mode re-base onto the new default (shift by the default's catalog-price
        // change) so it lands at 0 while keeping each row's manual edit.
        if (newValue.length > 0 && newValue.find((x) => x.isDefault) == null) {
            if (absolutePricing === true) {
                const delta = (newValue[0].product.defaultPrice ?? 0) - (item.product.defaultPrice ?? 0);
                newValue = newValue.map((x, i) => ({ ...x, isDefault: i === 0, price: x.price - delta }));
            } else {
                newValue = newValue.map((x, i) => ({ ...x, isDefault: i === 0 }));
            }
        }
        onChange(newValue);
    };

    const handleAdd = (item: ProjectCatalogProduct) => {
        const isFirst = value.length === 0;
        // Seed only the newly added row so existing rows (incl. manual edits) are kept. In
        // absolute mode offset it from the current default (or 0 if it becomes the default).
        let price = item.defaultPrice ?? 0;
        if (absolutePricing === true) {
            const defaultCatalogPrice = isFirst
                ? item.defaultPrice ?? 0
                : (value.find((x) => x.isDefault) ?? value[0])?.product.defaultPrice ?? 0;
            price = (item.defaultPrice ?? 0) - defaultCatalogPrice;
        }
        onChange([
            ...value,
            { product: item, price, isDefault: isFirst, isDeletable: true, isArchived: false },
        ]);
    };

    const handlePriceChange = (item: OptionGroupProductsEditItem, newPrice: number) => {
        onChange(arrayReplace(value, item, { ...item, price: newPrice }));
    };

    const handleArchiveChange = (item: OptionGroupProductsEditItem, archived: boolean) => {
        onChange(arrayReplace(value, item, { ...item, isArchived: archived }));
    };

    const handleSetDefault = (item: OptionGroupProductsEditItem) => {
        let newValue = value.map((x) => ({ ...x, isDefault: x === item }));
        // In absolute mode re-base offsets onto the new default: shift every row by the
        // change in the default's catalog price. Keeps the new default at 0 and preserves
        // each row's manual edit (unlike recomputing from catalog, which wipes edits).
        if (absolutePricing === true) {
            const oldDefault = value.find((x) => x.isDefault);
            const delta = (item.product.defaultPrice ?? 0) - (oldDefault?.product.defaultPrice ?? 0);
            if (delta !== 0) {
                newValue = newValue.map((x) => ({ ...x, price: x.price - delta }));
            }
        }
        // The standard product is placed first in the group. This is only the initial
        // placement; it can still be reordered afterwards and that order is what gets saved.
        const defaultIndex = newValue.findIndex((x) => x.isDefault);
        if (defaultIndex > 0) {
            newValue = arrayMove(newValue, defaultIndex, 0).map((x) => x!);
        }
        onChange(newValue);
    };

    const onSortEnd: SortEvent = ({ oldIndex, newIndex }) => {
        const newValue = arrayMove(value, oldIndex, newIndex).map((item, index) => ({ ...item!, sortOrder: index }));
        onChange(newValue);
    };

    const handleOrderByPrice = () => {
        const sortedValue = [...value]
            .sort((a, b) => a.price - b.price)
            .map((item, index) => ({ ...item, sortOrder: index }));

        onChange(sortedValue);
    };

    return (
        <div className={theme.container}>
            <div className={theme.productSelector}>
                <div className={theme.availableProductsContainerMobile}>
                    <Select<ProjectCatalogProduct>
                        formatOptionLabel={renderDropdownProduct}
                        getOptionLabel={(product) => product.name}
                        getOptionValue={(product) => String(product.id)}
                        className={theme.dropdown}
                        onChange={(x) => {
                            if (x && !isSelectMultiValue(x)) {
                                handleAdd(x);
                            }
                        }}
                        options={filteredProducts}
                        isMulti={false}
                        value={null}
                        placeholder={<Trans>Add a product...</Trans>}
                    />
                </div>

                <div className={theme.availableProductsContainer}>
                    <div className={theme.availableProductsHeader}>
                        <h2>
                            <Trans>Available products</Trans>
                        </h2>
                        <FilterButtons
                            searchText={filters.searchText ?? ''}
                            onSearchTextChange={(v) => mergeFilters({ searchText: v })}
                            onButtonClick={() => setIsFilterOpen((x) => !x)}
                            isActive={filterModified}
                            isLoading={isLoading}
                        />
                    </div>
                    <FilterPopOut
                        onClear={() => mergeFilters({ searchText: '', itemCategoryIds: null })}
                        onClose={() => setIsFilterOpen(false)}
                        isVisible={isFilterOpen}>
                        <label className="filter-label">
                            <Trans>Filter by category</Trans>
                        </label>
                        <ItemCategoryFilter
                            value={filters.itemCategoryIds ?? []}
                            onChange={(v) => mergeFilters({ itemCategoryIds: v.length > 0 ? v : null })}
                            maxDepth={1}
                        />
                    </FilterPopOut>
                    <div className={theme.scrollable}>
                        {isLoading ? (
                            <LoadingIndicator />
                        ) : (
                            <div className={theme.productList}>
                                {filteredProducts?.map((product) => (
                                    <AvailableProductCard key={product.id} product={product} onAdd={handleAdd} />
                                ))}
                                {filteredProducts.length === 0 && <NoItemsCard title={<Trans>No products found</Trans>} />}
                            </div>
                        )}
                    </div>
                </div>

                <div className={theme.selectedProductsContainer}>
                    <div className={theme.selectedProductsHeader}>
                        <h2>
                            <Trans>Selected products</Trans>
                        </h2>
                        <Button
                            variant="tertiary"
                            size="sm"
                            icon={faArrowDownWideShort}
                            onClick={handleOrderByPrice}
                            disabled={value.length <= 1}>
                            <Trans>Order by price</Trans>
                        </Button>
                    </div>
                    <div className={theme.scrollable}>
                        <Sortable
                            items={value}
                            idField={(x) => x.product.id}
                            onSortEnd={onSortEnd}
                            direction="vertical"
                            className={theme.productList}>
                            {(item, { handleProps, isDragging }) => (
                                <SelectedProductCard
                                    key={item.product.id}
                                    product={item}
                                    onRemove={handleRemove}
                                    onSetDefault={handleSetDefault}
                                    onPriceChange={handlePriceChange}
                                    onArchiveChange={handleArchiveChange}
                                    dragHandleProps={handleProps}
                                    selectedFeeSet={selectedFeeSet}
                                />
                            )}
                        </Sortable>
                    </div>
                </div>
            </div>
        </div>
    );
};
